import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
// @ts-ignore
import { List as VirtualizedList, CellMeasurer, CellMeasurerCache } from 'react-virtualized';
import classnames from 'classnames';
import { IOptionsBoxProps } from '../../type';
import './index.scss';

interface IDimensions {
  width: number;
  height: number;
}

const DynamicOptionBox = forwardRef<any, IOptionsBoxProps>((props, ref) => {
  const {
    virtual,
    listHeight: propsHeight = 600,
    options,
    rowHeight = 100,
    optionRender,
    resetScrollOnOptionsChange = false,
  } = props || {};

  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<VirtualizedList>(null);
  const [dimensions, setDimensions] = useState<IDimensions>({ width: 0, height: 0 });

  // 创建缓存来存储每行的高度
  const cacheRef = useRef(
    new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: rowHeight,
    })
  );

  // 暴露方法给父组件
  useImperativeHandle(ref, () => ({
    // 滚动到顶部
    scrollToTop: () => {
      if (listRef.current) {
        listRef.current.scrollToPosition(0);
      }
    },
    // 重新计算行高
    recomputeRowHeights: () => {
      cacheRef.current.clearAll();
      if (listRef.current) {
        listRef.current.forceUpdateGrid();
        listRef.current.recomputeRowHeights();
      }
    },
  }));

  // 只在列表项数量变化时重新计算行高（打钩/取消打钩不会触发）
  const optionsLengthRef = useRef(options?.length || 0);
  useEffect(() => {
    const newLength = options?.length || 0;
    const lengthChanged = optionsLengthRef.current !== newLength;
    
    if (lengthChanged) {
      optionsLengthRef.current = newLength;
      cacheRef.current.clearAll();
      if (listRef.current) {
        if (resetScrollOnOptionsChange) {
          listRef.current.scrollToPosition(0); // 只在指定时滚动到顶部
        }
        listRef.current.forceUpdateGrid();
        listRef.current.recomputeRowHeights();
      }
    } else {
      // 长度没变，只是内容变了（如打钩状态），只更新视图不重置滚动
      if (listRef.current) {
        listRef.current.forceUpdateGrid();
      }
    }
  }, [options, resetScrollOnOptionsChange]);

  // 获取容器尺寸
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { offsetWidth, offsetHeight } = containerRef.current;
        setDimensions({ 
          width: offsetWidth,
          height: offsetHeight || propsHeight 
        });
      }
    };

    // 初始化尺寸
    updateDimensions();

    // 监听窗口大小变化
    const resizeObserver = new ResizeObserver(updateDimensions);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [propsHeight]);

  if (!virtual) {
    return (
      <div className="dbh-transfer-modal-options-list">
        {options?.map((item: any) => {
          if (typeof optionRender === 'function') {
            return optionRender(item);
          }
          return null;
        })}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={classnames('dbh-transfer-modal-options-list', 'dbh-dynamic-option-box')}
      style={{ 
        height: '100%',
        width: '100%',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {dimensions.width > 0 && (
        <VirtualizedList
          ref={listRef}
          width={dimensions.width}
          height={dimensions.height}
          rowCount={options?.length || 0}
          rowHeight={cacheRef.current.rowHeight}
          deferredMeasurementCache={cacheRef.current}
          rowRenderer={({ index, key, parent, style }: any) => {
            const item = options?.[index];
            
            return (
              <CellMeasurer
                key={key}
                cache={cacheRef.current}
                parent={parent}
                columnIndex={0}
                rowIndex={index}
              >
                {({ registerChild }: any) => (
                  <div 
                    ref={registerChild} 
                    style={style}
                    className="dbh-virtual-row-wrapper"
                  >
                    {typeof optionRender === 'function'
                      ? optionRender(item)
                      : null}
                  </div>
                )}
              </CellMeasurer>
            );
          }}
          overscanRowCount={3}
          style={{
            outline: 'none'
          }}
        />
      )}
    </div>
  );
});

DynamicOptionBox.displayName = 'DynamicOptionBox';

export default DynamicOptionBox;
